import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge
} from "./UI";
import { HelpCircle, MessageCircle, FileText, ArrowRight, Clock, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { formatDateLong } from "utils/utilityFunctions";

export function SupportSection() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    try {
      const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
      const res = await axios.get(`${BASE_URL}/org/get/ticket/${orgId}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      const raw = res.data;
      const ticketsData = Array.isArray(raw.data) ? raw.data : raw.data?.tickets || [];

      // Map and take top 3
      const mappedTickets = ticketsData.map(ticket => ({
        id: ticket.ticket_number || `#TICKET-${ticket.id}`,
        subject: ticket.issue_header,
        status: ticket.status?.toLowerCase() || "created",
        date: formatDateLong(ticket.created_on)
      })).slice(0, 3);

      setTickets(mappedTickets);
    } catch (err) {
      console.error("❌ Error fetching tickets", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      created: "bg-gray-bg text-text-gray border-0",
      in_progress: "bg-sm-primary/10 text-sm-primary border-0",
      resolved: "bg-light-green text-dark-green border-0",
      on_hold: "bg-sm-accent/10 text-sm-accent border-0",
    };
    const labels = {
      created: "Created",
      in_progress: "In Progress",
      resolved: "Resolved",
      on_hold: "On Hold",
    };
    return (
      <Badge className={styles[status] || styles.created}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Support</h2>
        <p className="text-text-gray mt-1">Get help when you need it — we're here for you.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-sm-border/50 shadow-sm cursor-pointer hover:border-sm-primary/30 transition-all" onClick={() => navigate("/help")}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-sm-primary/10">
                <HelpCircle className="h-6 w-6 text-sm-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-primary-text">Help Center</h3>
                <p className="text-sm text-text-gray mt-1">Browse articles and guides to find answers quickly.</p>
                <Button variant="link" className="px-0 h-auto mt-2 text-sm-primary gap-1">
                  Browse Articles <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-sm-border/50 shadow-sm cursor-pointer hover:border-sm-accent/30 transition-all" onClick={() => navigate("/dashboard/raise-request")}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-sm-accent/10">
                <MessageCircle className="h-6 w-6 text-sm-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-primary-text">Raise a Ticket</h3>
                <p className="text-sm text-text-gray mt-1">Can't find what you need? Our team will help.</p>
                <Button
                  variant="link"
                  className="px-0 h-auto mt-2 text-sm-accent gap-1"
                  onClick={() => navigate("/dashboard/raise-request")}
                >
                  Create Ticket <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-sm-primary" />
                Recent Tickets
              </CardTitle>
              <CardDescription>Track your support requests.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/raise-request")}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-sm-primary" />
            </div>
          ) : tickets.length > 0 ? (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between p-4 rounded-lg border border-sm-border hover:bg-gray-bg/50 transition-colors cursor-pointer" onClick={() => navigate("/dashboard/raise-request")}>
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-gray-bg">
                      <Clock className="h-4 w-4 text-text-gray" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm-primary">{ticket.id}</span>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <p className="text-sm text-primary-text">{ticket.subject}</p>
                    </div>
                  </div>
                  <span className="text-sm text-text-gray">{ticket.date}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-text-gray">No recent tickets. That's great! 🎉</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm bg-gradient-to-r from-sm-primary/5 to-sm-secondary/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-sm-primary/10">
              <MessageCircle className="h-6 w-6 text-sm-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-primary-text">Need quick help?</h4>
              <p className="text-sm text-text-gray">Our team typically responds within 24-48 hours.</p>
            </div>
            <Button onClick={() => window.location.href = "mailto:contact@sitemaster.ai"}>
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
